const ACTIVE_BREWERY_STORAGE_PREFIX = 'brewhouse.activeBreweryId';

export interface ManageableBreweryRow {
  breweryId: string;
}

/**
 * localStorage key scoped to the signed-in user.
 */
export function activeBreweryStorageKey(userId: string): string {
  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
  if (!normalizedUserId) {
    return '';
  }
  return `${ACTIVE_BREWERY_STORAGE_PREFIX}.${normalizedUserId}`;
}

export function isAssignedBreweryId(
  breweries: ManageableBreweryRow[],
  breweryId: string,
): boolean {
  const normalizedId = typeof breweryId === 'string' ? breweryId.trim() : '';
  if (!normalizedId) {
    return false;
  }
  return breweries.some(
    (row) =>
      typeof row.breweryId === 'string' && row.breweryId.trim() === normalizedId,
  );
}

export function readStoredActiveBreweryId(userId: string): string | null {
  const storageKey = activeBreweryStorageKey(userId);
  if (!storageKey) {
    return null;
  }
  try {
    const stored = localStorage.getItem(storageKey);
    if (typeof stored !== 'string') {
      return null;
    }
    const trimmed = stored.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

export function writeStoredActiveBreweryId(userId: string, breweryId: string): void {
  const storageKey = activeBreweryStorageKey(userId);
  const normalizedId = typeof breweryId === 'string' ? breweryId.trim() : '';
  if (!storageKey || !normalizedId) {
    return;
  }
  try {
    localStorage.setItem(storageKey, normalizedId);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Priority: valid query param → valid localStorage → first assigned brewery.
 */
export function resolveInitialActiveBreweryId(
  breweries: ManageableBreweryRow[],
  queryBreweryId: string,
  userId: string,
): string | null {
  if (!Array.isArray(breweries) || breweries.length === 0) {
    return null;
  }

  const normalizedQuery =
    typeof queryBreweryId === 'string' ? queryBreweryId.trim() : '';
  if (normalizedQuery && isAssignedBreweryId(breweries, normalizedQuery)) {
    return normalizedQuery;
  }

  const storedId = readStoredActiveBreweryId(userId);
  if (storedId && isAssignedBreweryId(breweries, storedId)) {
    return storedId;
  }

  const firstId =
    typeof breweries[0]?.breweryId === 'string' ? breweries[0].breweryId.trim() : '';
  return firstId.length > 0 ? firstId : null;
}
